import { BuildingOffice2Icon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../../Avatar';
import { accountTypeTone } from '../accountTypeTone';
import { IdentityChip } from '../IdentityChip';
import { RiskStatusPill } from '../../shared/RiskStatusPill';
import { formatCreatedDate } from '../../../utils/dates';
import type { PracticeDetail } from '../../../services/practiceDetail';
import { PracticeMetaCell } from './PracticeMetaCell';
import { isEnabledDetailTab, type DetailTabDef, type DetailTabId } from './detailTabs';

function doctorDisplayName(practice: PracticeDetail): string | null {
    const first = practice.doctor?.drFirstName?.trim();
    const last = practice.doctor?.drLastName?.trim();
    if (!first && !last) return null;
    return `Dr. ${[first, last].filter(Boolean).join(' ')}`;
}

function Breadcrumb({ name }: { name: string }) {
    const navigate = useNavigate();
    return (
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500 min-w-0">
            <button
                type="button"
                onClick={() => navigate('/accounts/practices')}
                className="hover:text-gray-800 transition-colors"
            >
                Accounts
            </button>
            <ChevronRightIcon className="h-3 w-3 text-gray-300" />
            <button
                type="button"
                onClick={() => navigate('/accounts/practices')}
                className="hover:text-gray-800 transition-colors"
            >
                Practices
            </button>
            <ChevronRightIcon className="h-3 w-3 text-gray-300" />
            <span className="font-semibold text-gray-700 truncate">{name}</span>
        </div>
    );
}

// Reproduces SidebarLayout's default header row (this page hides it via
// `hideHeader` - see PracticeDetail.tsx) with the breadcrumb standing in for the title.
function TopStrip({ name }: { name: string }) {
    return (
        <div className="flex items-center justify-between px-8 pt-4 pb-2 shrink-0">
            <Breadcrumb name={name} />
            <div className="w-10 h-10">
                <Avatar />
            </div>
        </div>
    );
}

function IdentityChips({ practice }: { practice: PracticeDetail }) {
    const chips = [
        practice.account?.accountType && {
            label: practice.account.accountType,
            tone: accountTypeTone(practice.account.accountType)
        },
        practice.account?.network && { label: practice.account.network, tone: 'neutral' as const },
        practice.account?.version && { label: practice.account.version, tone: 'neutral' as const }
    ].filter((chip): chip is { label: string; tone: ReturnType<typeof accountTypeTone> } =>
        Boolean(chip)
    );
    if (chips.length === 0) return null;
    return (
        <div className="flex items-center gap-1.5 shrink-0">
            {chips.map((chip) => (
                <IdentityChip key={chip.label} tone={chip.tone}>
                    {chip.label}
                </IdentityChip>
            ))}
        </div>
    );
}

interface TabBarProps {
    tabs: readonly DetailTabDef[];
    activeTab: DetailTabId;
    onChange: (tab: DetailTabId) => void;
    // Only the Calls tab has a count slot for now - zero means "no count" so
    // a bare 0 doesn't show up next to every empty practice's Calls tab.
    callCount: number;
}

function TabBar({ tabs, activeTab, onChange, callCount }: TabBarProps) {
    return (
        <div className="px-6 flex gap-6 border-t border-gray-100">
            {tabs.map((tab) => {
                const isActive = tab.id === activeTab;
                const isEnabled = isEnabledDetailTab(tab.id);
                return (
                    <button
                        key={tab.id}
                        type="button"
                        disabled={!isEnabled}
                        onClick={() => onChange(tab.id as DetailTabId)}
                        title={isEnabled ? undefined : 'Coming soon'}
                        className={`pt-3 pb-3 text-sm border-b-2 flex items-center gap-1.5 ${
                            isActive
                                ? 'font-semibold text-gray-900 border-gray-900'
                                : isEnabled
                                  ? 'font-medium text-gray-500 border-transparent hover:text-gray-700'
                                  : 'font-medium text-gray-300 border-transparent cursor-not-allowed'
                        }`}
                    >
                        {tab.label}
                        {/* Same pill treatment as PatientFiles's Notes tab count -
                            dark when active, muted otherwise. */}
                        {tab.id === 'communications' && callCount > 0 && (
                            <span
                                className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full tabular-nums ${
                                    isActive
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                {callCount}
                            </span>
                        )}
                        {!isEnabled && (
                            <span className="text-[10px] font-semibold text-gray-400 border border-gray-200 rounded px-1">
                                SOON
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

interface PracticeDetailHeaderProps {
    practice: PracticeDetail;
    onAddField: (fieldKey: string) => void;
    tabs: readonly DetailTabDef[];
    activeTab: DetailTabId;
    onTabChange: (tab: DetailTabId) => void;
}

// Attention counters and the "Nothing outstanding" pill are omitted: that data
// doesn't exist yet. Same reasoning for the practice-switcher chevron - no switch mechanism.
export function PracticeDetailHeader({
    practice,
    onAddField,
    tabs,
    activeTab,
    onTabChange
}: PracticeDetailHeaderProps) {
    const doctorName = doctorDisplayName(practice);
    const city = practice.address?.city;
    const state = practice.address?.state;
    const hasLocation = Boolean(city && state);
    const phone = practice.contact?.phone;
    const email = practice.contact?.email;

    return (
        <>
            <TopStrip name={practice.name} />
            <div className="px-8 pb-2 mt-6 shrink-0">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)]">
                    <div className="px-6 pt-4 pb-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                            <BuildingOffice2Icon className="h-4 w-4" />
                        </div>
                        {/* Chips sit shrink-0 beside the name so a long name - not the
                            chips - gives up space first (PracticesTable's name cell). */}
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <h1
                                className="text-[20px] leading-tight font-bold text-gray-900 truncate min-w-0"
                                title={practice.name}
                            >
                                {practice.name}
                            </h1>
                            <IdentityChips practice={practice} />
                            {practice.risk && (
                                <RiskStatusPill
                                    riskStatus={practice.risk.riskStatus}
                                    riskNote={practice.risk.riskNote}
                                />
                            )}
                        </div>
                    </div>
                    <div className="px-6 pb-4 grid grid-cols-6 gap-4">
                        <PracticeMetaCell
                            label="Doctor"
                            missing={!doctorName}
                            onAdd={() => onAddField('drFirstName')}
                        >
                            {doctorName}
                        </PracticeMetaCell>
                        <PracticeMetaCell
                            label="Location"
                            missing={!hasLocation}
                            onAdd={() => onAddField('city')}
                        >
                            {city}, {state}
                        </PracticeMetaCell>
                        <PracticeMetaCell
                            label="Phone"
                            missing={!phone}
                            onAdd={() => onAddField('phone')}
                        >
                            {phone}
                        </PracticeMetaCell>
                        <PracticeMetaCell
                            label="Email"
                            missing={!email}
                            onAdd={() => onAddField('email')}
                        >
                            {email}
                        </PracticeMetaCell>
                        <PracticeMetaCell label="Patients">
                            {practice.patientCount}
                        </PracticeMetaCell>
                        <PracticeMetaCell label="Added">
                            {formatCreatedDate(practice.createdAt)}
                        </PracticeMetaCell>
                    </div>
                    <TabBar
                        tabs={tabs}
                        activeTab={activeTab}
                        onChange={onTabChange}
                        callCount={practice.callCount}
                    />
                </div>
            </div>
        </>
    );
}
